// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddToCartButton from "@steiner/App/Components/AddToCartButton/AddToCartButton";
import TableCounter from "@steiner/App/Components/Common/TableCounter";
import { sharedDateTimeOptions } from "@steiner/XDL/constants";
import { isNonEmptyString } from "@steiner/XDL/Utilities";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showInvoiceNumber = "showInvoiceNumber",
    showInvoiceDate = "showInvoiceDate",
    showTerms = "showTerms",
    showDueDate = "showDueDate",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showInvoiceTotal = "showInvoiceTotal",
    showCurrentBalance = "showCurrentBalance",
    // XNG-Changes
    showReleaseNumber = "showReleaseNumber",
    showOrderedBy = "showOrderedBy",
    showBillTrustLink = "showBillTrustLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showInvoiceNumber]: boolean;
        [fields.showInvoiceDate]: boolean;
        [fields.showTerms]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showInvoiceTotal]: boolean;
        [fields.showCurrentBalance]: boolean;
        // XNG-Changes
        [fields.showReleaseNumber]: boolean;
        [fields.showOrderedBy]: boolean;
        [fields.showBillTrustLink]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    getInvoicesParameter: state.pages.invoiceHistory.getInvoicesParameter,
    failedToLoadInvoices: state.data.invoices.failedToLoadInvoices,
    language: state.context.session.language,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryTableStyles {
    container?: InjectableCss;
    headerClickables?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    invoiceDateHeader?: DataTableHeaderProps;
    termsHeader?: DataTableHeaderProps;
    dueDateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    poNumberHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    currentBalanceHeader?: DataTableHeaderProps;
    invoiceNumberCell?: DataTableCellBaseProps;
    invoiceDateCell?: DataTableCellBaseProps;
    termsCell?: DataTableCellBaseProps;
    dueDateCell?: DataTableCellBaseProps;
    shipToCell?: DataTableCellBaseProps;
    poNumberCell?: DataTableCellBaseProps;
    statusCell?: DataTableCellBaseProps;
    totalCell?: DataTableCellBaseProps;
    currentBalanceCell?: DataTableCellBaseProps;
    // XNG-Chagnes
    payLinkWrapper?: InjectableCss;
    payLink?: LinkPresentationProps;
}

export const tableStyles: InvoiceHistoryTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    headerClickables: {
        css: css`
            &:focus {
                outline-style: none;
            }
            width: 100%;
            justify-content: space-between;
            display: block;
        `, // TODO ISC-12604 focus removal is incorrect
    },
    totalHeader: {
        alignX: "right",
    },
    currentBalanceHeader: {
        alignX: "right",
    },
    totalCell: {
        alignX: "right",
    },
    currentBalanceCell: {
        alignX: "right",
    },
};

const styles = tableStyles;

export const labels = {
    invoiceNumber: translate("Invoice #"),
    invoiceDate: translate("Invoice Date"),
    terms: translate("Terms"),
    dueDate: translate("Due Date"),
    shipTo: translate("Ship To"),
    releaseNumber: translate("Release #"),
    customerPO: translate("PO #"),
    orderedBy: translate("Ordered By"),
    status: translate("Status"),
    invoiceTotal: translate("Invoice Total"),
    currentBalance: translate("Current Balance"),
    actions: translate("Actions"),
};

const InvoiceHistoryTable = (props: Props) => {
    const headerClick = (sortField: string) => {
        const sort = props.getInvoicesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        props.updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (props.getInvoicesParameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.getInvoicesParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const openBillTrustLink = (billTrustLink: string) => {
        if (billTrustLink) {
            window.open(billTrustLink, "_blank");
        }
    }

    if (props.failedToLoadInvoices) {
        return null;
    }

    const invoicesDataView = useContext(InvoicesDataViewContext);
    if (!invoicesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (invoicesDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No invoices found")}</Typography>
            </StyledWrapper>
        );
    }

    const rows = invoicesDataView.value.map(invoice => {
        return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            date: invoice.invoiceDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.invoiceDate),
                      language: props.language,
                      // XNG-Changes: Fix OOTB Date Format
                      options: {
                          ...sharedDateTimeOptions,
                      },
                  })
                : "",
            terms: invoice.terms,
            dueDate: invoice.dueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.dueDate),
                      language: props.language,
                      // XNG-Changes: Fix OOTB Date Format
                      options: {
                          ...sharedDateTimeOptions,
                      },
                  })
                : "",
            shipTo: `${invoice.stCompanyName}`,
            customerPO: invoice.customerPO,
            status: invoice.status,
            total: invoice.invoiceTotalDisplay,
            currentBalance: invoice.currentBalanceDisplay,
            // XNG-Changes
            orderedBy: invoice.properties.orderedBy,
            releaseNumber: invoice.properties.releaseNumber,
            billTrustLink: invoice.properties.billTrustLink,
        };
    });

    const {
        showInvoiceNumber,
        showInvoiceDate,
        showTerms,
        showDueDate,
        showShipTo,
        showPONumber,
        showStatus,
        showInvoiceTotal,
        showCurrentBalance,
        // XNG-Changes
        showReleaseNumber,
        showOrderedBy,
        showBillTrustLink,
    } = props.fields;

    const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

    const pagination = invoicesDataView.value && invoicesDataView.pagination ? invoicesDataView.pagination : undefined;
    return (
        <StyledWrapper {...styles.container}>
            <TableCounter pagination={pagination} countText={translate("Invoices")} accordionOnSmallScreen={false}>
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.dataTableHead}>
                        {isTrueOrUndefined(showInvoiceNumber) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("invoiceNumber")}
                                {...styles.invoiceNumberHeader}
                                onSortClick={() => headerClick("invoiceNumber")}
                            >
                                {labels.invoiceNumber}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showInvoiceDate) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("invoiceDate")}
                                {...styles.invoiceDateHeader}
                                onSortClick={() => headerClick("invoiceDate")}
                                data-test-selector="invoiceHistory_invoiceHeader_date"
                            >
                                {labels.invoiceDate}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showTerms) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("terms")}
                                {...styles.termsHeader}
                                onSortClick={() => headerClick("terms")}
                            >
                                {labels.terms}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showDueDate) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("dueDate")}
                                {...styles.dueDateHeader}
                                onSortClick={() => headerClick("dueDate")}
                            >
                                {labels.dueDate}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showStatus) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("status")}
                                {...styles.statusHeader}
                                onSortClick={() => headerClick("status")}
                            >
                                {labels.status}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showShipTo) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("stCompanyName")}
                                {...styles.shipToHeader}
                                onSortClick={() => headerClick("stCompanyName")}
                            >
                                {labels.shipTo}
                            </DataTableHeader>
                        )}
                        {/* XNG-Changes */}
                        {isTrueOrUndefined(showReleaseNumber) && (
                            <DataTableHeader tight {...styles.poNumberHeader}>
                                {labels.releaseNumber}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showPONumber) && (
                            <DataTableHeader
                                tight
                                sorted={sorted("customerPO")}
                                {...styles.poNumberHeader}
                                onSortClick={() => headerClick("customerPO")}
                            >
                                {labels.customerPO}
                            </DataTableHeader>
                        )}
                        {/* XNG-Changes */}
                        {isTrueOrUndefined(showOrderedBy) && (
                            <DataTableHeader tight {...styles.poNumberHeader}>
                                {labels.orderedBy}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showInvoiceTotal) && props.showPricing && (
                            <DataTableHeader
                                tight
                                sorted={sorted("invoiceTotal")}
                                {...styles.totalHeader}
                                onSortClick={() => headerClick("invoiceTotal")}
                            >
                                {labels.invoiceTotal}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showCurrentBalance) && props.showPricing && (
                            <DataTableHeader
                                tight
                                sorted={sorted("currentBalance")}
                                {...styles.currentBalanceHeader}
                                onSortClick={() => headerClick("currentBalance")}
                            >
                                {labels.currentBalance}
                            </DataTableHeader>
                        )}
                        {/* XNG-Changes */}
                        {isTrueOrUndefined(showBillTrustLink) && (
                            <DataTableHeader tight {...styles.currentBalanceHeader}></DataTableHeader>
                        )}
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody}>
                        {rows.map(
                            ({
                                id,
                                invoiceNumber,
                                date,
                                terms,
                                dueDate,
                                shipTo,
                                customerPO,
                                status,
                                total,
                                currentBalance,
                                // XNG-Changes
                                orderedBy,
                                releaseNumber,
                                billTrustLink,
                            }) => (
                                <DataTableRow
                                    key={id}
                                    {...styles.dataTableRow}
                                    data-test-selector="invoiceHistory_invoiceLine"
                                    data-test-key={id}
                                >
                                    {isTrueOrUndefined(showInvoiceNumber) && (
                                        <DataTableCell
                                            {...styles.invoiceNumberCell}
                                            data-test-selector="invoiceHistory_invoiceLine_number"
                                            {...{ header: labels.invoiceNumber }}
                                        >
                                            <InvoiceDetailPageTypeLink
                                                title={invoiceNumber}
                                                invoiceNumber={invoiceNumber}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showInvoiceDate) && (
                                        <DataTableCell
                                            {...styles.invoiceDateCell}
                                            data-test-selector="invoiceHistory_invoiceLine_date"
                                            {...{ header: labels.invoiceDate }}
                                        >
                                            <InvoiceDetailPageTypeLink
                                                title={date}
                                                invoiceNumber={invoiceNumber}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showTerms) && (
                                        <DataTableCell {...styles.termsCell} {...{ header: labels.terms }}>
                                            {terms}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showDueDate) && (
                                        <DataTableCell {...styles.dueDateCell} {...{ header: labels.dueDate }}>
                                            <InvoiceDetailPageTypeLink
                                                title={dueDate}
                                                invoiceNumber={invoiceNumber}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showStatus) && (
                                        <DataTableCell {...styles.statusCell} {...{ header: labels.status }}>
                                            {status}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showShipTo) && (
                                        <DataTableCell {...styles.shipToCell} {...{ header: labels.shipTo }}>
                                            {shipTo}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showReleaseNumber) && (
                                        <DataTableCell {...styles.poNumberCell} {...{ header: labels.releaseNumber }}>
                                            {releaseNumber}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showPONumber) && (
                                        <DataTableCell {...styles.poNumberCell} {...{ header: labels.customerPO }}>
                                            {customerPO}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderedBy) && (
                                        <DataTableCell {...styles.poNumberCell} {...{ header: labels.orderedBy }}>
                                            {orderedBy}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showInvoiceTotal) && props.showPricing && (
                                        <DataTableCell {...styles.totalCell} {...{ header: labels.invoiceTotal }}>
                                            {total}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showCurrentBalance) && props.showPricing && (
                                        <DataTableCell
                                            {...styles.currentBalanceCell}
                                            {...{ header: labels.currentBalance }}
                                        >
                                            {currentBalance}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showBillTrustLink) && (
                                        <DataTableCell {...styles.currentBalanceCell} {...{ header: translate(" ") }}>
                                            <StyledWrapper {...styles.payLinkWrapper}>
                                                {isNonEmptyString(billTrustLink) ? (
                                                    <AddToCartButton
                                                        onClick={() => openBillTrustLink(billTrustLink)}
                                                        label={translate("Pay")}
                                                    />
                                                ) : (
                                                    <>--</>
                                                )}
                                            </StyledWrapper>
                                        </DataTableCell>
                                    )}
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </TableCounter>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryTable),
    definition: {
        group: "Invoice History",
        displayName: "Search Results Table",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showInvoiceDate,
                displayName: "Invoice Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showTerms,
                displayName: "Terms",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showInvoiceTotal,
                displayName: "Invoice Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCurrentBalance,
                displayName: "Current Balance",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showReleaseNumber,
                displayName: "Release Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showOrderedBy,
                displayName: "Ordered By",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showBillTrustLink,
                displayName: "Bill Trust Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
